import React, { useState, useEffect } from 'react';
import AdminBaseLayout from '../../components/layouts/AdminBaseLayout';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Box,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Link
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HistoryIcon from '@mui/icons-material/History';
import withAuthAdmin from '../../middleware/useWithAuthAdmin';
import AdminHttpService from '../../api/AdminHttpService';
import LoadingOverlay from '../../components/LoadingOverlay';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';

function AdminView() {
  const [admins, setAdmins] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [createDialog, setCreateDialog] = useState({
    open: false,
    name: '',
    email: ''
  });

  const fetchAdmins = async (pageNumber = 1, searchTerm = '') => {
    try {
      setLoading(true);
      const response = await AdminHttpService.getAdmins(pageNumber, searchTerm);
      setAdmins(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total);
      setRowsPerPage(response.pagination.per_page);
    } catch (error) {
      setError(error.message || 'Erro ao buscar administradores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins(page + 1, search);
  }, [page, search]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleDeleteClick = (admin) => {
    setAdminToDelete(admin);
  };

  const handleCloseDeleteDialog = () => {
    setAdminToDelete(null);
  };

  const handleDelete = async () => {
    if (!adminToDelete) return;

    try {
      setLoading(true);
      await AdminHttpService.deleteAdmin(adminToDelete.id);
      setSuccess('Administrador excluído com sucesso');
      handleCloseDeleteDialog();
      fetchAdmins(page + 1, search);
    } catch (error) {
      setError(error.message || 'Erro ao deletar administrador');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setCreateDialog({
      open: true,
      name: '',
      email: ''
    });
  };

  const handleCreateCancel = () => {
    setCreateDialog({
      open: false,
      name: '',
      email: ''
    });
  };

  const handleCreateConfirm = async () => {
    try {
      setLoading(true);
      await AdminHttpService.createAdmin(createDialog.name, createDialog.email);
      fetchAdmins(page + 1, search);
      handleCreateCancel();
      setSuccess('Administrador criado com sucesso');
    } catch (error) {
      setError(error.message || 'Erro ao criar administrador');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseError = () => {
    setError('');
  };

  const handleCloseSuccess = () => {
    setSuccess('');
  };

  return (
    <AdminBaseLayout>
      <LoadingOverlay open={loading} />
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <DeleteConfirmationModal
        open={!!adminToDelete}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDelete}
        title="Excluir Administrador"
        message="Tem certeza que deseja excluir este administrador? Esta ação não pode ser desfeita."
        itemName={adminToDelete?.name}
      />

      <Dialog
        open={createDialog.open}
        onClose={handleCreateCancel}
        aria-labelledby="create-dialog-title"
      >
        <DialogTitle id="create-dialog-title">
          Convidar Administrador
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Nome"
              type="text"
              fullWidth
              value={createDialog.name}
              onChange={(e) => setCreateDialog({ ...createDialog, name: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              value={createDialog.email}
              onChange={(e) => setCreateDialog({ ...createDialog, email: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateCancel} color="primary">
            Cancelar
          </Button>
          <Button 
            onClick={handleCreateConfirm} 
            color="primary" 
            variant="contained"
            disabled={!createDialog.name || !createDialog.email}
          >
            Convidar
          </Button>
        </DialogActions>
      </Dialog>

      <Paper elevation={3} sx={{ p: { xs: 0.5, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          mb: 2,
          gap: 1,
          px: { xs: 1, sm: 0 }
        }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.2rem', sm: '2rem' } }}>
            Gerenciar Administradores
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateClick}
            sx={{ whiteSpace: 'nowrap', fontSize: { xs: '0.8rem', sm: '1rem' } }}
          >
            Convidar Administrador
          </Button>
        </Box>

        <Box sx={{ mb: 1, px: { xs: 1, sm: 0 } }}>
          <TextField
            fullWidth
            label="Buscar administradores"
            variant="outlined"
            value={search}
            onChange={handleSearchChange}
            size="small"
            sx={{ 
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.8rem', sm: '1rem' }
              },
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.8rem', sm: '1rem' },
                padding: { xs: '8px', sm: '14px' }
              }
            }}
          />
        </Box>

        <Box sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ 
            maxHeight: 'calc(100vh - 400px)',
            width: '100%',
            overflowX: 'auto'
          }}>
            <Table stickyHeader size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.4)' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    whiteSpace: 'nowrap',
                    padding: { xs: '4px 2px', sm: '16px' },
                    fontSize: { xs: '0.75rem', sm: '1rem' }
                  }}>ID</TableCell>
                  <TableCell sx={{ 
                    whiteSpace: 'nowrap',
                    padding: { xs: '4px 2px', sm: '16px' },
                    fontSize: { xs: '0.75rem', sm: '1rem' }
                  }}>Nome</TableCell>
                  <TableCell sx={{ 
                    whiteSpace: 'nowrap',
                    padding: { xs: '4px 2px', sm: '16px' },
                    fontSize: { xs: '0.75rem', sm: '1rem' }
                  }}>Email</TableCell>
                  <TableCell align="right" sx={{ 
                    whiteSpace: 'nowrap',
                    padding: { xs: '4px 2px', sm: '16px' },
                    fontSize: { xs: '0.75rem', sm: '1rem' }
                  }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell sx={{ 
                      whiteSpace: 'nowrap',
                      padding: { xs: '4px 2px', sm: '16px' },
                      fontSize: { xs: '0.75rem', sm: '1rem' }
                    }}>{admin.id}</TableCell>
                    <TableCell sx={{ 
                      whiteSpace: 'nowrap',
                      padding: { xs: '4px 2px', sm: '16px' },
                      fontSize: { xs: '0.75rem', sm: '1rem' }
                    }}>{admin.name}</TableCell>
                    <TableCell sx={{ 
                      whiteSpace: 'nowrap',
                      padding: { xs: '4px 2px', sm: '16px' },
                      fontSize: { xs: '0.75rem', sm: '1rem' }
                    }}>{admin.email}</TableCell>
                    <TableCell align="right" sx={{ 
                      whiteSpace: 'nowrap',
                      padding: { xs: '4px 2px', sm: '16px' },
                      fontSize: { xs: '0.75rem', sm: '1rem' }
                    }}>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(admin)}
                        size="small"
                        sx={{ padding: { xs: '2px', sm: '8px' } }}
                      >
                        <DeleteIcon sx={{ fontSize: { xs: '1rem', sm: '1.5rem' } }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
            sx={{
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: { xs: '0.75rem', sm: '1rem' }
              },
              '.MuiTablePagination-select': {
                fontSize: { xs: '0.75rem', sm: '1rem' }
              }
            }}
          />
        </Box>
      </Paper>

    </AdminBaseLayout>
  );
}

export default withAuthAdmin(AdminView);
